import { useMemo, useState } from 'react'
import { Workbook } from 'exceljs'
import { Check, Download, Filter, Search } from 'lucide-react'
import { Card } from '@/components/ui'
import { formatNumber } from '@/lib/utils'
import type { StudioChurnRiskRow } from '@/services/clientLifecycleService'

interface ChurnRiskTableProps {
  rows: StudioChurnRiskRow[]
  loading: boolean
  error: string | null
  idleDaysMin: number
  idleDaysMax: number
}

function formatDisplayDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  return match ? `${match[3]}-${match[2]}-${match[1]}` : value
}

function formatSubscriptionName(row: StudioChurnRiskRow): string {
  return row.subscriptionNames.length > 0 ? row.subscriptionNames.join(', ') : '-'
}

function getSubscriptionStatus(row: StudioChurnRiskRow): { label: string; className: string } {
  if (row.subscriptionStatus === 'none') {
    return {
      label: 'Не найден базовый абонемент',
      className: 'bg-slate-100 text-slate-600',
    }
  }

  if (row.subscriptionStatus === 'active') {
    return { label: 'Активен', className: 'bg-emerald-100 text-emerald-700' }
  }

  if (row.subscriptionStatus === 'frozen') {
    return { label: 'Заморожен', className: 'bg-blue-100 text-blue-700' }
  }

  if (row.subscriptionStatus === 'missing' && row.subscriptionDaysMissing) {
    const days = row.subscriptionDaysMissing
    return {
      label: `Истёк ${days} ${days === 1 ? 'день' : days < 5 ? 'дня' : 'дней'} назад`,
      className: 'bg-red-100 text-red-700',
    }
  }

  return { label: 'Истёк', className: 'bg-red-100 text-red-700' }
}

type SubscriptionStatusFilter = 'active' | 'frozen' | 'expired' | 'none'

const SUBSCRIPTION_STATUS_FILTERS: Array<{ value: SubscriptionStatusFilter; label: string }> = [
  { value: 'active', label: 'Активен' },
  { value: 'frozen', label: 'Заморожен' },
  { value: 'expired', label: 'Истёк' },
  { value: 'none', label: 'Не найден базовый абонемент' },
]

function getSubscriptionStatusFilter(row: StudioChurnRiskRow): SubscriptionStatusFilter {
  if (row.subscriptionStatus === 'active') return 'active'
  if (row.subscriptionStatus === 'frozen') return 'frozen'
  if (row.subscriptionStatus === 'none') return 'none'
  return 'expired'
}

export function ChurnRiskTable({ rows, loading, error, idleDaysMin, idleDaysMax }: ChurnRiskTableProps) {
  const [query, setQuery] = useState('')
  const [sortKey, setSortKey] = useState<'visits' | 'idle' | 'expired'>('visits')
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<SubscriptionStatusFilter[]>([])

  const toggleStatusFilter = (value: SubscriptionStatusFilter) => {
    setSelectedStatusFilters((current) =>
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
    )
  }

  const filteredRows = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const filteredBySearch = needle
      ? rows.filter((row) =>
          [row.clientFio, row.clientExternalId, row.favoriteDirection, row.lastSubscriptionName, ...row.subscriptionNames]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(needle))
        )
      : rows
    const filtered = selectedStatusFilters.length === 0
      ? filteredBySearch
      : filteredBySearch.filter((row) => selectedStatusFilters.includes(getSubscriptionStatusFilter(row)))

    return [...filtered].sort((a, b) => {
      if (sortKey === 'idle') return b.daysIdle - a.daysIdle
      if (sortKey === 'expired') {
        const statusRank = { missing: 0, expired: 1, frozen: 2, active: 3, none: 4 } as const
        const rankDifference = statusRank[a.subscriptionStatus] - statusRank[b.subscriptionStatus]
        if (rankDifference !== 0) return rankDifference
        if (a.subscriptionStatus === 'missing' && b.subscriptionStatus === 'missing') {
          return (b.subscriptionDaysMissing ?? 0) - (a.subscriptionDaysMissing ?? 0)
        }
        return b.daysIdle - a.daysIdle
      }
      return (
        b.visits0to30 + b.visits31to60 + b.visits61to90
        - (a.visits0to30 + a.visits31to60 + a.visits61to90)
      )
    })
  }, [query, rows, selectedStatusFilters, sortKey])

  const exportExcel = async () => {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet('Клиенты для возврата')
    worksheet.columns = [
      { header: 'Клиент', key: 'client', width: 30 },
      { header: 'Посещения 61–90 дней', key: 'visits61to90', width: 22 },
      { header: 'Посещения 31–60 дней', key: 'visits31to60', width: 22 },
      { header: 'Посещения 0–30 дней', key: 'visits0to30', width: 22 },
      { header: 'Последний визит', key: 'lastVisit', width: 18 },
      { header: 'Дней нет', key: 'daysIdle', width: 12 },
      { header: 'Любимое направление', key: 'direction', width: 26 },
      { header: 'Последний базовый/безлимитный абонемент', key: 'subscriptionName', width: 36 },
      { header: 'Статус абонемента', key: 'subscriptionStatus', width: 24 },
    ]
    worksheet.getRow(1).font = { bold: true }
    worksheet.views = [{ state: 'frozen', ySplit: 1 }]
    filteredRows.forEach((row) => {
      worksheet.addRow({
        client: row.clientFio,
        visits61to90: row.visits61to90,
        visits31to60: row.visits31to60,
        visits0to30: row.visits0to30,
        lastVisit: formatDisplayDate(row.lastVisitDate),
        daysIdle: row.daysIdle,
        direction: row.favoriteDirection || '',
        subscriptionName: formatSubscriptionName(row),
        subscriptionStatus: getSubscriptionStatus(row).label,
      })
    })

    const buffer = await workbook.xlsx.writeBuffer()
    const url = URL.createObjectURL(new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'studio-churn-risk.xlsx'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Card className="p-3.5">
      <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-base font-semibold">Клиенты, которых стоит вернуть</h2>
          <p className="text-xs text-muted-foreground">
            Были визиты по базовому абонементу и не приходили {idleDaysMin}–{idleDaysMax} дней. Статус берём из истории ежедневных выгрузок.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <label className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="w-full rounded-lg border border-border bg-card py-2 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary sm:w-64"
              placeholder="Поиск"
            />
          </label>
          <select
            value={sortKey}
            onChange={(event) => setSortKey(event.target.value as 'visits' | 'idle' | 'expired')}
            className="rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="visits">Сначала больше посещений</option>
            <option value="idle">Сначала дольше без посещений</option>
            <option value="expired">Сначала раньше истёк</option>
          </select>
          <button
            type="button"
            onClick={() => void exportExcel()}
            disabled={filteredRows.length === 0}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-border px-3 py-2 text-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Excel
          </button>
        </div>
      </div>

      {error ? <p className="text-sm text-error">{error}</p> : null}
      {loading ? <p className="py-8 text-center text-sm text-muted-foreground">Загрузка списка...</p> : null}
      {!loading && !error && filteredRows.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {selectedStatusFilters.length > 0 ? 'Нет клиентов с выбранными статусами' : 'Спящих клиентов нет'}
        </p>
      ) : null}

      {!loading && !error && filteredRows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1200px] table-fixed text-sm">
            <colgroup>
              <col className="w-[18%]" />
              <col className="w-[6%]" />
              <col className="w-[6%]" />
              <col className="w-[6%]" />
              <col className="w-[10%]" />
              <col className="w-[9%]" />
              <col className="w-[14%]" />
              <col className="w-[20%]" />
              <col className="w-[11%]" />
            </colgroup>
            <thead className="bg-muted text-foreground">
              <tr className="text-left text-xs">
                <th rowSpan={2} className="px-2 py-2 font-medium">Клиент</th>
                <th colSpan={3} className="px-2 py-2 text-center font-medium text-foreground">Посещения до последнего визита</th>
                <th rowSpan={2} className="px-2 py-2 text-center font-medium">Последний визит</th>
                <th rowSpan={2} className="px-2 py-2 text-center font-medium">Дней без посещений</th>
                <th rowSpan={2} className="px-2 py-2 font-medium">Любимое направление</th>
                <th rowSpan={2} className="px-2 py-2 font-medium">Последний базовый/безлимитный абонемент</th>
                <th rowSpan={2} className="relative px-2 py-2 font-medium">
                  <details className="group relative inline-block">
                    <summary
                      className={`flex cursor-pointer list-none items-center gap-1.5 whitespace-nowrap rounded-md outline-none [&::-webkit-details-marker]:hidden ${selectedStatusFilters.length > 0 ? 'text-primary' : ''}`}
                    >
                      <span>Статус абонемента</span>
                      <span
                        aria-label="Фильтр по статусу абонемента"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors group-open:bg-background group-open:text-foreground hover:bg-background/80"
                      >
                        <Filter className="h-3.5 w-3.5" />
                      </span>
                    </summary>
                    <div className="absolute right-0 top-full z-30 mt-1 w-60 rounded-lg border border-border bg-card p-2 text-left text-sm shadow-lg">
                      <div className="mb-1 flex items-center justify-between px-2 py-1">
                        <span className="font-medium">Статус абонемента</span>
                        {selectedStatusFilters.length > 0 ? (
                          <button
                            type="button"
                            onClick={() => setSelectedStatusFilters([])}
                            className="text-xs text-muted-foreground hover:text-foreground"
                          >
                            Сбросить
                          </button>
                        ) : null}
                      </div>
                      {SUBSCRIPTION_STATUS_FILTERS.map((option) => {
                        const checked = selectedStatusFilters.includes(option.value)
                        return (
                          <label
                            key={option.value}
                            className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleStatusFilter(option.value)}
                              className="sr-only"
                            />
                            <span
                              aria-hidden="true"
                              className={`flex h-4 w-4 items-center justify-center rounded border ${checked ? 'border-primary bg-primary text-primary-foreground' : 'border-border'}`}
                            >
                              {checked ? <Check className="h-3 w-3" /> : null}
                            </span>
                            <span>{option.label}</span>
                          </label>
                        )
                      })}
                    </div>
                  </details>
                </th>
              </tr>
              <tr className="text-xs">
                <th className="border-b border-border px-2 py-1 text-center font-medium text-foreground">61–90 дней</th>
                <th className="border-b border-border px-2 py-1 text-center font-medium text-foreground">31–60 дней</th>
                <th className="border-b border-border px-2 py-1 text-center font-medium text-foreground">0–30 дней</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.clientKey} className="border-b border-border/70">
                  <td className="px-2 py-2">
                    <div className="font-medium">{row.clientFio}</div>
                  </td>
                  <td className="px-2 py-2 text-center tabular-nums font-semibold">{formatNumber(row.visits61to90)}</td>
                  <td className="px-2 py-2 text-center tabular-nums font-semibold">{formatNumber(row.visits31to60)}</td>
                  <td className="px-2 py-2 text-center tabular-nums font-semibold">{formatNumber(row.visits0to30)}</td>
                  <td className="px-2 py-2 text-center tabular-nums">{formatDisplayDate(row.lastVisitDate)}</td>
                  <td className="px-2 py-2 text-center tabular-nums font-semibold text-amber-700">{formatNumber(row.daysIdle)}</td>
                  <td className="max-w-[190px] truncate px-2 py-2">{row.favoriteDirection || '-'}</td>
                  <td className="max-w-[220px] px-2 py-2">{formatSubscriptionName(row)}</td>
                  <td className="px-2 py-2">
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getSubscriptionStatus(row).className}`}>
                      {getSubscriptionStatus(row).label}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

    </Card>
  )
}
